import React, {memo, useCallback, useRef, useState} from "react";
import {USER_DEFAULT_AVTAR_URL} from "../../../containers/RegistrationLoginPage/utils";
import {getImageUrlBySize, prependDomainToImageUrl} from "../../../utils/image";
import Image from "../../HTMLEntities/Image";
import UserImageUploader from "../../UserImageUploader/Loadable";
import ImageSelectorInput from "../../UserImageUploader/widgets/ImageSelectorInput";
import {
    EditProfileUserPhoto,
    AddPhotoLink,
    UserImageLinkSection,
    RemoveDiv,
    CameraButton,
    OrDiv,
} from "./Styled";
import ChangeRemoveLayer from "../../UserImageUploader/widgets/ChangeRemoveLayer";
import {useSelector} from "react-redux";
import {RootStore} from "../../../configureStore";
import {RemoveIcon} from "../../ShikshaApply/ConsentForm/widgets/Styled";
import {CROP_WINDOW_HEIGHT, CROP_WINDOW_WIDTH} from "../../UserImageUploader/utils";
import CameraCapture from "../../CameraCapture/Loadable";

interface UserImageProps {
    hideImage?: boolean;
    hideRemoveLink?: boolean;
    hideAddChangeButton?: boolean;
    addChangeButtonHeading?: string;
    viewType?: "editProfile" | "consentForm";
    rff?: Record<string, unknown>;
    onRemoveImage?: () => void;
    cropShape?: "rect" | "round";
    getImageBlobURL?: (url: string) => void;
    enableUserImageUpload?: boolean;
    enableCameraClick?: boolean;
    image?: string;
    debug?: boolean;
    cropSize?: {width: number; height: number};
}

const UserImage: React.FC<UserImageProps> = ({
    hideImage = false,
    hideRemoveLink = false,
    hideAddChangeButton = false,
    viewType = "editProfile",
    addChangeButtonHeading,
    rff,
    onRemoveImage,
    cropShape = "round",
    getImageBlobURL,
    enableUserImageUpload = true,
    image,
    enableCameraClick = false,
    cropSize = {width: CROP_WINDOW_WIDTH, height: CROP_WINDOW_HEIGHT},
}: UserImageProps) => {
    const [fileURL, setFileURL] = useState<string>();
    const [cameraMode, setCameraMode] = useState(false);

    let avtarUrl = useSelector(
        (state: RootStore) => state.layout.userAndShortlistData?.userBasicDetail?.avtarUrl,
    );
    avtarUrl = avtarUrl?.indexOf("profileDefaultNew1") !== -1 ? null : avtarUrl;

    const addOrChangeInputRef = useRef<HTMLInputElement | null>(null);
    const removeChangeInputRef = useRef<HTMLInputElement | null>(null);
    const [showRemovePopUp, setShowRemovePopUp] = useState(false);

    const onPopUpClose = useCallback(() => {
        setFileURL("");
        const addOrChangeInput = addOrChangeInputRef.current;
        if (addOrChangeInput) {
            addOrChangeInput.value = "";
        }
        const removeChangeInput = removeChangeInputRef.current;
        if (removeChangeInput) {
            removeChangeInput.value = "";
        }
        setCameraMode(false);
    }, []);
    const onCapturePhoto = useCallback((blobURL: string) => {
        setFileURL(blobURL);
        setCameraMode(false);
    }, []);

    const onClickCamera = useCallback(() => {
        setCameraMode(true);
    }, []);

    const removePhotoHandle = useCallback(() => {
        setShowRemovePopUp(true);
    }, []);

    const removeChangePhotoHandle = useCallback((url: string) => {
        setTimeout(() => {
            setFileURL(url);
        }, 50);
        setShowRemovePopUp(false);
    }, []);

    const onFileChange = useCallback((url: string) => {
        setFileURL(url);
    }, []);

    const showChangeAndRemove = !enableUserImageUpload ? !!image : !!avtarUrl;
    const imageURL =
        !enableUserImageUpload && image
            ? image
            : prependDomainToImageUrl(
                  getImageUrlBySize(avtarUrl ?? USER_DEFAULT_AVTAR_URL, "medium"),
              );

    const onRemove = useCallback(() => {
        if (onRemoveImage) {
            onRemoveImage();
        }
    }, [onRemoveImage]);

    return (
        <>
            <EditProfileUserPhoto consentFormStyle={viewType === "consentForm"}>
                {!hideImage && <Image src={imageURL} alt={"user image"} />}
                <UserImageLinkSection consentFormStyle={viewType === "consentForm"}>
                    {!hideAddChangeButton && (
                        <AddPhotoLink>
                            <ImageSelectorInput
                                heading={
                                    addChangeButtonHeading
                                        ? addChangeButtonHeading
                                        : showChangeAndRemove
                                        ? "Change Photo"
                                        : "Add Photo"
                                }
                                id="addChangePhoto"
                                type={"text"}
                                ref={addOrChangeInputRef}
                                onChange={onFileChange}
                                consentFormStyle={viewType === "consentForm"}
                                rff={rff}
                            />
                        </AddPhotoLink>
                    )}
                    {!hideAddChangeButton && enableCameraClick && (
                        <>
                            <OrDiv>or</OrDiv>
                            <CameraButton onClick={onClickCamera}>Click Photo</CameraButton>
                        </>
                    )}
                    {!hideRemoveLink && showChangeAndRemove && (
                        <RemoveDiv
                            onClick={removePhotoHandle}
                            hideAddChangeButton={hideAddChangeButton}
                        >
                            {viewType === "consentForm" ? <RemoveIcon /> : "Remove"}
                        </RemoveDiv>
                    )}
                </UserImageLinkSection>
                {fileURL && !cameraMode && (
                    <UserImageUploader
                        key={fileURL}
                        fileURL={fileURL}
                        onPopUpClose={onPopUpClose}
                        cropShape={cropShape}
                        enableUserImageUpload={enableUserImageUpload}
                        getImageBlobURL={getImageBlobURL}
                        cropSize={cropSize}
                    />
                )}
                {cameraMode && (
                    <CameraCapture
                        onCapturePhoto={onCapturePhoto}
                        onClose={() => setCameraMode(false)}
                        // setCameraMode={setCameraMode}
                    />
                )}
            </EditProfileUserPhoto>
            {showRemovePopUp && (
                <ChangeRemoveLayer
                    removeChangeInputRef={removeChangeInputRef}
                    removeChangePhotoHandle={removeChangePhotoHandle}
                    setShowRemovePopUp={setShowRemovePopUp}
                    onRemoveImage={onRemove}
                    enableUserImageUpload={enableUserImageUpload}
                />
            )}
        </>
    );
};
export default memo(UserImage);
